import { FreelancerInvoiceSubmissionResource } from '@/types/resources';
import { PaymentMethod, recipientType } from '@/utils/enums';

import { InvoiceHtmlProps } from './components/InvoiceHtml';
import { amountPaymentPurpose } from './invoiceTotals';

/**
 * Seller details the invoice document needs. Both `useUser().freelancer` and
 * the submission's embedded `freelancer` satisfy this.
 */
export type InvoiceFreelancerDetails = {
  name?: string | null;
  country?: string;
  vat_number?: string;
  address?: string;
  post_code?: string;
  company_address?: string;
  activity_code?: string;
  personal_code?: string;
  paypal_email?: string;
  payoneer_email?: string;
};

/**
 * Money and hours come back from the API as strings on some fields despite
 * being typed as numbers, so everything handed to the document is coerced.
 */
const num = (value: unknown): number => Number(value) || 0;

const optionalNum = (value: unknown): number | undefined =>
  value == null || value === '' ? undefined : num(value);

/** Can the seller block be filled in, or would the preview be incomplete? */
export const canPreviewSubmission = (
  freelancer?: InvoiceFreelancerDetails,
): boolean => !!freelancer?.country;

/**
 * Builds the invoice document props from a stored submission, mirroring what
 * the generator form assembles before submitting.
 */
const submissionToInvoiceProps = (
  submission: FreelancerInvoiceSubmissionResource,
  freelancer?: InvoiceFreelancerDetails,
): InvoiceHtmlProps => {
  const isVatPayer = !!freelancer?.vat_number;

  return {
    currency: submission.currency,
    amount: num(submission.amount),
    hours: optionalNum(submission.hours),
    bonus: optionalNum(submission.bonus),
    paymentPurpose: submission.paymentPurpose ?? '',
    invoiceNumber:
      submission.invoiceNumber ||
      submission.invoice?.invoice_number ||
      undefined,
    freelancerName: freelancer?.name ?? '',
    freelancerCountry: freelancer?.country ?? '',
    freelancerVatNumber: freelancer?.vat_number,
    // VAT payers invoice from their company address when they have one.
    freelancerAddress: isVatPayer
      ? freelancer?.company_address ?? freelancer?.address
      : freelancer?.address,
    freelancerPostCode:
      isVatPayer && freelancer?.company_address
        ? undefined
        : freelancer?.post_code,
    freelancerActivityCode: freelancer?.activity_code,
    freelancerPersonalCode: freelancer?.personal_code,
    bankIban: submission.bankAccount?.iban ?? '',
    bankName: submission.bankAccount?.bank ?? '',
    bankSwift: submission.bankAccount?.swift_code,
    trueReceiver: submission.bankAccount?.true_receiver,
    isCompanyReceiver:
      submission.bankAccount?.recipient_type === recipientType.COMPANY,
    paymentMethod: submission.paymentMethod,
    walletEmail:
      submission.paymentMethod === PaymentMethod.Payoneer
        ? freelancer?.payoneer_email
        : freelancer?.paypal_email,
    vat: num(submission.vat),
    date: submission.createdAt.split('T')[0],
    amounts: submission.amounts?.map((a) => ({
      amount: num(a.amount),
      hours: optionalNum(a.hours),
      paymentPurpose: amountPaymentPurpose(a),
    })),
    bonuses: submission.bonuses?.map((b) => ({
      amount: num(b.amount),
      purpose: b.purpose ?? undefined,
    })),
    buyer: submission.company,
  };
};

export default submissionToInvoiceProps;
